import React, { useEffect, useState, useRef } from 'react';
import {
  X, ExternalLink, FileText, Save, Edit3, Upload, DollarSign, Clock, MapPin,
  User, Calendar, Briefcase, Star, CheckCircle, AlertCircle, Heart, Flag,
  Globe, Building2, Users, TrendingUp, Award, Link2, Copy, Check
} from 'lucide-react';
import useAuthStore from '../store/authStore'
import { useTenant } from '../contexts/TenantContext';
import configs from '../config'
import { createProposal } from '../services/proposalService';
import { getUserPortfolio } from '../services/portfolioService';
import { FileUpload } from './FileUpload';
import { ProposalForm } from './ProposalForm';
import { ModalNavigation } from './ModalNavigation';
import { fetchWithTenant } from '../utils/apiClient';
import { saveJob, unsaveJob, isJobSaved } from '../services/savedJobsService';
import { trackEvent } from '../contexts/PostHogProvider';
import { trackFirstProposalCreated } from '../utils/analyticsHelper';
import { getRelativeTime, getRelativeTimeFromHours, parseDateInput } from '../utils/dateUtils';

export function JobDetailsModal({ job, onClose, proposalStatus = null, onSaveToggle, currentIndex = -1, totalCount = 0, onNavigate = null }) {
  const { user } = useAuthStore();
  const { tenant } = useTenant();
  const isFirstProposal = useRef(null);

  const [isGenerating, setIsGenerating] = useState(false);
  const [coverLetter, setCoverLetter] = useState(null);
  const [coverLetterId, setCoverLetterId] = useState(null);
  const [coverLetterPresent, setCoverLetterPresent] = useState(false);
  const [error, setError] = useState(null);
  const [isSaved, setIsSaved] = useState(false);
  const [savingJob, setSavingJob] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  // New state for inline editing
  const [isEditing, setIsEditing] = useState(false);
  const [editedCoverLetter, setEditedCoverLetter] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Proposal creation state
  const [showProposalForm, setShowProposalForm] = useState(false);
  const [portfolio, setPortfolio] = useState([]);
  const [loadingPortfolio, setLoadingPortfolio] = useState(false);
  const [proposalData, setProposalData] = useState({
    title: '',
    description: '',
    coverLetter: '',
    pricing: {
      type: 'fixed',
      amount: 0,
      currency: 'USD'
    },
    attachments: [],
    portfolioItems: [],
    links: [],
    screeningAnswers: []
  });
  const [coverLetterSource, setCoverLetterSource] = useState('ai'); // 'ai', 'custom'
  const [isCreatingProposal, setIsCreatingProposal] = useState(false);
  const [applyOnUpworkMode, setApplyOnUpworkMode] = useState(false);

  const coverLetterRef = useRef(null);
  const textareaRef = useRef(null);

  // Check if job is saved on mount
  useEffect(() => {
    if (job?._id) {
      isJobSaved(job._id).then(setIsSaved).catch(() => setIsSaved(false));
    }
  }, [job?._id]);

  // Track job viewed
  useEffect(() => {
    if (job?._id) {
      trackEvent('job_viewed', {
        job_id: job._id,
        job_title: job.title,
        contract_type: job.contractTerms?.contractType,
        budget: job.amount?.displayValue || job.amount?.value,
        has_screening_questions: (job.contractorSelection?.proposalRequirement?.screeningQuestions?.length || 0) > 0,
      });
    }
  }, [job?._id]);

  useEffect(() => {
    const getCoverLetter = async () => {
      try {
        const res = await fetchWithTenant(`${configs.BACKEND_URL}/api/coverLetter/get-coverLetter/${job._id}`, {
          method: "GET",
        })

        const data = await res.json()

        if (res.status == 200) {
          setCoverLetterPresent(true)
          setCoverLetter(data.coverLetter)
          setCoverLetterId(data._id)
        }

        if (!data.coverLetter) setCoverLetterPresent(false)
      } catch (error) {
        setError("Unknown error")
      }
    }

    getCoverLetter()
  }, [job.coverLetterId])

  const generateCoverLetter = async () => {
    setIsGenerating(true)
    setError(null)
    setCoverLetter('')
    setCoverLetterPresent(true)
    trackEvent('cover_letter_generation_started', { job_id: job._id });

    try {
      const res = await fetchWithTenant(`${configs.BACKEND_URL}/api/coverLetter/generate-stream/${job._id}`, {
        method: "POST",
      })

      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        setError(data.error || "Error while generating cover letter")
        setCoverLetterPresent(false)
        setIsGenerating(false)
        return
      }

      const reader = res.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''
      let accumulated = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop()

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue
          try {
            const event = JSON.parse(line.slice(6))
            if (event.type === 'chunk') {
              accumulated += event.text
              setCoverLetter(accumulated)
            } else if (event.type === 'done') {
              setCoverLetterId(event._id)
              trackEvent('cover_letter_generation_completed', { job_id: job._id });
            } else if (event.type === 'error') {
              setError(event.message)
            }
          } catch {
            // skip malformed events
          }
        }
      }
    } catch {
      setError("Failed to generate cover letter. Please try again.")
      setCoverLetterPresent(false)
    }
    setIsGenerating(false)
  }

  const updateCoverLetter = async () => {
    if (!coverLetter) {
      await generateCoverLetter()
      return
    }

    setIsGenerating(true)
    setError(null)

    try {
      const res = await fetchWithTenant(`${configs.BACKEND_URL}/api/coverLetter/update-coverLetter/${job._id}/${coverLetterId}`, {
        method: "PUT",
      })

      const data = await res.json()

      if (res.status !== 200) {
        setError("Error while updating cover letter")
      } else {
        setCoverLetter(data.coverLetter)
      }
    } catch {
      setError("Failed to regenerate cover letter. Please try again.")
    }
    setIsGenerating(false)
  }

  const handleDoubleClick = () => {
    if (coverLetter) {
      setIsEditing(true);
      setEditedCoverLetter(coverLetter);
      // Focus the textarea after a short delay to ensure it's rendered
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
        }
      }, 100);
    }
  };



  const handleSave = async () => {
    if (!editedCoverLetter.trim()) return;

    // Don't save if no changes were made
    if (editedCoverLetter === coverLetter) {
      setIsEditing(false);
      return;
    }

    setIsSaving(true);
    try {
      const res = await fetchWithTenant(`${configs.BACKEND_URL}/api/coverLetter/inline-update-coverLetter/${job._id}/${coverLetterId}`, {
        method: "PUT",
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ coverLetterText: editedCoverLetter })
      });

      const data = await res.json();

      if (res.status === 200) {
        setCoverLetter(data.coverLetter);
        setIsEditing(false);
        setError(null);
      } else {
        setError("Error while saving cover letter");
      }
    } catch (error) {
      setError("Unknown error while saving");
    }
    setIsSaving(false);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedCoverLetter('');
    setError(null);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      handleCancel();
    } else if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleSave();
    }
  };

  // Load portfolio projects
  const loadPortfolio = async () => {
    try {
      setLoadingPortfolio(true);
      const response = await getUserPortfolio({ status: 'active', isPublic: true });
      // Handle both response formats: { success: true, data: [...] } or direct array
      if (response.success && response.data) {
        setPortfolio(response.data);
      } else if (Array.isArray(response)) {
        setPortfolio(response);
      } else if (response.data && Array.isArray(response.data)) {
        setPortfolio(response.data);
      } else {
        setPortfolio([]);
      }
    } catch {
      setPortfolio([]);
    } finally {
      setLoadingPortfolio(false);
    }
  };

  // Proposal creation functions
  const handleCreateProposal = () => {
    trackEvent('proposal_creation_started', { job_id: job._id, has_cover_letter: !!coverLetter });
    // Load portfolio projects when creating proposal
    loadPortfolio();

    // Get screening questions from the correct path
    const screeningQuestions = job.contractorSelection?.proposalRequirement?.screeningQuestions || [];

    setProposalData(prev => ({
      ...prev,
      title: `Proposal for ${job.title}`,
      description: job.description?.substring(0, 200) + '...' || '',
      pricing: {
        type: job.budget?.type,
        amount: job.budget?.min,
        currency: job.budget?.currency
      },
      coverLetter: coverLetterSource === 'ai' && coverLetter ? coverLetter.coverLetter || coverLetter : '',
      screeningAnswers: screeningQuestions.map(q => ({
        questionId: q.id,
        question: q.question,
        answer: ''
      })),
      // Ensure these are set for the proposal
      jobPostingId: job._id,
      upworkJobId: job.jobId || job._id
    }));
    setShowProposalForm(true);
  };

  const handleProposalInputChange = (field, value) => {
    setProposalData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCoverLetterSourceChange = (source) => {
    setCoverLetterSource(source);
    if (source === 'ai' && coverLetter) {
      setProposalData(prev => ({
        ...prev,
        coverLetter: coverLetter.coverLetter
      }));
    } else if (source === 'custom') {
      setProposalData(prev => ({
        ...prev,
        coverLetter: ''
      }));
    }
  };

  // Load cover letter when proposal form opens
  useEffect(() => {
    if (showProposalForm && coverLetter) {
      const coverLetterText = coverLetter.coverLetter || coverLetter;
      setProposalData(prev => ({
        ...prev,
        coverLetter: coverLetterText
      }));
    }
  }, [showProposalForm, coverLetter]);

  const handleFileUpload = (files) => {
    // Files are already converted to base64 format by FileUpload component
    setProposalData(prev => ({
      ...prev,
      attachments: files
    }));
  };

  const handleRemoveAttachment = (index) => {
    setProposalData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  const handlePortfolioSelection = (portfolioId, isSelected) => {
    setProposalData(prev => {
      const currentPortfolio = prev.portfolioItems || [];
      let updatedPortfolio;

      if (isSelected) {
        // Add portfolio item
        const portfolioItem = portfolio.find(p => p._id === portfolioId);
        if (portfolioItem) {
          updatedPortfolio = [...currentPortfolio, {
            portfolioId: portfolioItem._id,
            title: portfolioItem.title,
            description: portfolioItem.description,
            url: portfolioItem.projectUrl,
            imageUrl: portfolioItem.imageUrl
          }];
        }
      } else {
        // Remove portfolio item
        updatedPortfolio = currentPortfolio.filter(item => item.portfolioId !== portfolioId);
      }

      return {
        ...prev,
        portfolioItems: updatedPortfolio
      };
    });
  };

  const handleSubmitProposal = async (e) => {
    e.preventDefault();
    try {
      setIsCreatingProposal(true);

      // Check if user is authenticated
      if (!user) {
        setError('You must be logged in to create a proposal');
        return;
      }

      // Validate required fields
      if (!proposalData.title || !proposalData.description || !proposalData.coverLetter) {
        setError('Please fill in all required fields (title, description, cover letter)');
        return;
      }

      // Validate pricing
      if (!proposalData.pricing || !proposalData.pricing.amount) {
        setError('Please set a price for your proposal');
        return;
      }

      // Prepare proposal data with correct job references
      const proposalPayload = {
        title: proposalData.title,
        description: proposalData.description,
        coverLetter: proposalData.coverLetter,
        pricing: {
          type: proposalData.pricing.type || 'fixed',
          amount: Number(proposalData.pricing.amount),
          currency: proposalData.pricing.currency
        },
        attachments: proposalData.attachments || [],
        portfolioItems: proposalData.portfolioItems || [],
        links: proposalData.links || [],
        screeningAnswers: proposalData.screeningAnswers || [],
        jobPostingId: job._id, // Use the job's MongoDB _id
        upworkJobId: job.jobId || job._id // Use jobId if available, fallback to _id
      };

      // Pre-open window before async call to avoid popup blocker
      const applyWindow = applyOnUpworkMode ? window.open('', '_blank') : null;

      const response = await createProposal(proposalPayload);

      if (response.success) {
        // Check if this is the first proposal
        if (isFirstProposal.current === null) {
          try {
            const proposalsResponse = await fetchWithTenant(
              `${configs.BACKEND_URL}/api/proposals?source=app&mine=true&page=1&limit=1`
            );
            const proposalsData = await proposalsResponse.json();
            isFirstProposal.current = (proposalsData?.pagination?.total || 0) === 1;
          } catch {
            isFirstProposal.current = false;
          }
        }

        // Track first proposal created (onboarding funnel event)
        if (isFirstProposal.current) {
          trackFirstProposalCreated({
            userId: user?._id,
            role: user?.role,
            tenantId: tenant?._id,
            proposalId: response.data?._id,
            jobId: job._id,
            hasCoverLetter: !!proposalData.coverLetter,
          });
        }

        // Legacy event for backwards compatibility
        trackEvent('proposal_created', {
          job_id: job._id,
          pricing_type: proposalData.pricing?.type,
          has_cover_letter: !!proposalData.coverLetter,
          portfolio_items_count: proposalData.portfolioItems?.length || 0,
          has_screening_answers: (proposalData.screeningAnswers?.filter(a => a.answer)?.length || 0) > 0,
          apply_on_upwork: applyOnUpworkMode,
        });
        if (applyOnUpworkMode) {
          // Navigate the pre-opened window to Upwork apply page
          const applyUrl = getUpworkApplyUrl();
          if (applyWindow && applyUrl) {
            applyWindow.location.href = applyUrl;
          } else if (applyWindow) {
            applyWindow.close();
          }
          setShowProposalForm(false);
          setApplyOnUpworkMode(false);
        } else {
          setShowProposalForm(false);
          onClose();
        }
      } else {
        if (applyWindow) applyWindow.close();
        setError(response.error || 'Failed to create proposal');
      }
    } catch (error) {
      if (applyWindow) applyWindow.close();
      // Handle specific error types
      if (error.message.includes('Payload too large') || error.message.includes('413')) {
        setError('Files are too large. Please reduce file sizes or upload fewer files at once. Maximum total size is 50MB.');
      } else if (error.message.includes('Failed to fetch')) {
        setError('Network error. Please check your connection and try again.');
      } else {
        setError('Failed to create proposal: ' + error.message);
      }
    } finally {
      setIsCreatingProposal(false);
    }
  };

  const getNestedValue = (obj, path, defaultValue = '') => {
    if (!obj) return defaultValue;
    const value = path.split('.').reduce((acc, part) => acc && acc[part], obj);
    return value !== undefined ? value : defaultValue;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (e) {
      return dateString;
    }
  };

  const getSkillDisplay = (skill) => {
    if (typeof skill === 'string') return skill;
    if (typeof skill === 'object' && skill !== null) {
      return skill.name || skill.value || JSON.stringify(skill);
    }
    return '';
  };

  // Get contract type
  const getContractType = () => {
    const contractType = job.contractTerms?.contractType;
    if (!contractType) return null;
    if (contractType === 'HOURLY') return 'Hourly';
    if (contractType === 'FIXED_PRICE') return 'Fixed-Price';
    return contractType;
  };

  // Format budget/rate
  const formatBudget = () => {
    const contractType = getContractType();
    const isHourly = contractType === 'Hourly';

    if (isHourly) {
      const minRate = job.hourlyBudgetMin?.displayValue;
      const maxRate = job.hourlyBudgetMax?.displayValue;
      if (minRate && maxRate) {
        const cleanMin = minRate.replace(/^\$/, '');
        const cleanMax = maxRate.replace(/^\$/, '');
        return `$${cleanMin} - $${cleanMax}`;
      }
      const hourlyTerms = job.contractTerms?.hourlyContractTerms;
      if (hourlyTerms?.hourlyBudgetMin && hourlyTerms?.hourlyBudgetMax) {
        return `$${hourlyTerms.hourlyBudgetMin} - $${hourlyTerms.hourlyBudgetMax}`;
      }
    }

    const amount = job.amount || job.budget;
    if (!amount) return null;

    if (typeof amount === 'object') {
      if (amount.displayValue) return amount.displayValue;
      if (amount.value) return `$${amount.value}`;
      if (amount.min && amount.max) return `$${amount.min} - $${amount.max}`;
    } else if (typeof amount === 'number') {
      return `$${amount}`;
    } else if (typeof amount === 'string') {
      return amount;
    }
    return null;
  };

  // Get weekly hours
  const getWeeklyHours = () => {
    if (job.engagement) return job.engagement;
    const engagementType = job.contractTerms?.hourlyContractTerms?.engagementType;
    if (engagementType === 'FULL_TIME') return 'More than 30 hrs/week';
    if (engagementType === 'PART_TIME') return 'Less than 30 hrs/week';
    return null;
  };

  // Format experience level
  const formatExperienceLevel = (level) => {
    if (!level) return null;
    const levelStr = typeof level === 'object' ? level.prefLabel : level;
    if (!levelStr) return null;
    const enumMap = {
      'ENTRY_LEVEL': 'Entry Level',
      'ENTRY': 'Entry Level',
      'INTERMEDIATE': 'Intermediate',
      'EXPERT': 'Expert'
    };
    return enumMap[levelStr.toUpperCase()] ||
           levelStr.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  // Get proposal count display
  const getProposalCount = () => {
    const count = job.totalApplicants || job.proposalsTier?.proposalsReceived;
    if (!count && count !== 0) return null;
    if (typeof count === 'string') return count;
    if (count === 0) return 'Be the first to apply';
    if (count < 5) return 'Less than 5';
    if (count < 10) return '5 to 10';
    if (count < 15) return '10 to 15';
    if (count < 20) return '15 to 20';
    if (count < 50) return '20 to 50';
    return '50+';
  };

  // Format total spent
  const formatTotalSpent = (spent) => {
    if (!spent) return null;
    let value = typeof spent === 'object' ? (spent.value || spent.displayValue) : spent;
    if (!value) return null;

    // If it's already formatted (like "$14K"), return as-is
    if (typeof value === 'string' && value.includes('$')) return value;

    // Parse and format
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return null;

    if (numValue >= 1000000) return `$${(numValue / 1000000).toFixed(1)}M+`;
    if (numValue >= 1000) return `$${Math.round(numValue / 1000)}K+`;
    return `$${numValue}`;
  };

  // Handle save/unsave job
  const handleSaveToggle = async () => {
    if (savingJob) return;
    try {
      setSavingJob(true);
      if (isSaved) {
        await unsaveJob(job._id);
        setIsSaved(false);
        trackEvent('job_unsaved', { job_id: job._id });
      } else {
        await saveJob(job._id);
        setIsSaved(true);
        trackEvent('job_saved', { job_id: job._id });
      }
      onSaveToggle?.();
    } catch {
      // Silent — save toggle is non-critical
    } finally {
      setSavingJob(false);
    }
  };

  // Get the proper Upwork job URL
  // Upwork URLs use format /jobs/~02{numericId} - the "02" prefix is required
  const getUpworkJobUrl = () => {
    if (!job.jobId) return null;
    // Remove any existing ~ or 02 prefix to get clean numeric ID
    let cleanId = job.jobId;
    if (cleanId.startsWith('~')) cleanId = cleanId.substring(1);
    if (cleanId.startsWith('02')) cleanId = cleanId.substring(2);
    // Prepend 02 to the numeric ID
    return `https://www.upwork.com/jobs/~02${cleanId}`;
  };

  // Get the Upwork apply page URL from the job URL
  const getUpworkApplyUrl = () => {
    if (!job.jobId) return null;
    let cleanId = job.jobId;
    if (cleanId.startsWith('~')) cleanId = cleanId.substring(1);
    if (cleanId.startsWith('02')) cleanId = cleanId.substring(2);
    return `https://www.upwork.com/nx/proposals/job/~02${cleanId}/apply/`;
  };

  // Save a draft proposal for the extension to pick up, then open Upwork apply page
  const saveDraftForExtension = async () => {
    const coverLetterText = coverLetter?.coverLetter || coverLetter || '';
    const proposalPayload = {
      title: `Proposal for ${job.title}`,
      description: job.description?.substring(0, 200) + '...' || '',
      coverLetter: coverLetterText,
      pricing: {
        type: job.contractTerms?.contractType === 'Fixed' ? 'fixed' : 'hourly',
        amount: job.amount?.value || 0,
        currency: 'USD'
      },
      screeningAnswers: [],
      jobPostingId: job._id,
      upworkJobId: job.jobId || job._id
    };

    try {
      await createProposal(proposalPayload);
    } catch (err) {
      // If proposal already exists (409), that's fine — extension will find it
      if (!err.message?.includes('409') && !err.message?.includes('already')) {
        console.error('[Propel] Failed to save draft for extension:', err);
      }
    }
  };

  // Handle "Apply on Upwork" — smart routing based on screening questions
  const handleApplyOnUpwork = async () => {
    const screeningQuestions = getScreeningQuestions();
    const applyUrl = getUpworkApplyUrl();
    trackEvent('apply_on_upwork_clicked', { job_id: job._id, has_screening_questions: screeningQuestions.length > 0 });

    if (screeningQuestions.length > 0) {
      // Has screening questions — open proposal form modal so user can answer them
      setApplyOnUpworkMode(true);
      handleCreateProposal();
    } else {
      // No screening questions — open Upwork immediately, save draft in background
      // window.open must be synchronous with user click to avoid popup blocker
      window.open(applyUrl, '_blank');
      saveDraftForExtension();
    }
  };

  // Copy job link
  const copyJobLink = () => {
    const jobUrl = getUpworkJobUrl() || window.location.href;
    navigator.clipboard.writeText(jobUrl);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  // Get location display (country)
  const getLocation = () => {
    if (job.client?.location?.country) return job.client.location.country;
    if (job.client?.location?.city) return job.client.location.city;
    if (job.location) return typeof job.location === 'string' ? job.location : job.location.country;
    return 'Worldwide';
  };

  // Get city with local time
  const getCityWithTime = () => {
    const city = job.client?.location?.city;
    const timezone = job.client?.location?.timezone;
    if (!city && !timezone) return null;

    let localTime = '';
    if (timezone) {
      try {
        const now = new Date();
        localTime = now.toLocaleTimeString('en-US', {
          timeZone: timezone,
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        });
      } catch (e) {
        // Invalid timezone, skip time display
      }
    }

    if (city && localTime) return `${city} ${localTime}`;
    if (city) return city;
    return null;
  };

  // Get project type (ongoing vs one-time)
  const getProjectType = () => {
    // Check engagementDuration for project length hints
    const duration = job.duration || job.engagementDuration?.label;
    if (!duration) return null;

    // Common patterns for ongoing projects
    const ongoingPatterns = ['ongoing', 'long term', 'long-term', 'more than 6 months', '6+ months'];
    const durationLower = duration.toLowerCase();

    for (const pattern of ongoingPatterns) {
      if (durationLower.includes(pattern)) {
        return 'Ongoing project';
      }
    }

    // If it has a specific short duration, it's likely one-time
    const oneTimePatterns = ['1 to 3 months', 'less than 1 month', '1-3 months', '3 to 6 months', '3-6 months'];
    for (const pattern of oneTimePatterns) {
      if (durationLower.includes(pattern)) {
        return 'One-time project';
      }
    }

    return null;
  };

  // Get screening questions
  const getScreeningQuestions = () => {
    return job.contractorSelection?.proposalRequirement?.screeningQuestions || [];
  };

  // Get last client activity for "Last viewed by client"
  const getLastClientActivity = () => {
    const lastActivity = job.activityStats?.lastClientActivity;
    if (!lastActivity) return null;
    return getRelativeTime(lastActivity);
  };

  // Render star rating (visual 5 stars)
  const renderStars = (rating) => {
    if (!rating) return null;
    const fullStars = Math.floor(rating);
    const hasHalf = rating % 1 >= 0.5;
    const stars = [];

    for (let i = 0; i < 5; i++) {
      if (i < fullStars) {
        stars.push(<Star key={i} className="w-3.5 h-3.5 fill-yellow-400 text-yellow-400" />);
      } else if (i === fullStars && hasHalf) {
        stars.push(<Star key={i} className="w-3.5 h-3.5 fill-yellow-400/50 text-yellow-400" />);
      } else {
        stars.push(<Star key={i} className="w-3.5 h-3.5 text-gray-300" />);
      }
    }
    return <div className="flex">{stars}</div>;
  };

  // Derived values
  const contractType = getContractType();
  const budget = formatBudget();
  const weeklyHours = getWeeklyHours();
  const experienceLevel = formatExperienceLevel(job.experienceLevel || job.contractorTier);
  const duration = job.duration || job.projectLength;
  const proposalCount = getProposalCount();
  const clientRating = job.client?.rating || job.client?.totalFeedback;
  const clientTotalSpent = formatTotalSpent(job.client?.totalSpent);
  const paymentVerified = job.client?.verificationStatus === 'VERIFIED' || job.paymentVerified;
  const projectType = getProjectType();
  const screeningQuestions = getScreeningQuestions();
  const lastClientActivity = getLastClientActivity();
  const hoursInactive = Number(job?.relevance?.hoursInactive);
  const syncTimestamp = parseDateInput(job?.updatedAt || job?.createdAt);
  const elapsedSinceSyncHours = syncTimestamp
    ? Math.max(0, (Date.now() - syncTimestamp.getTime()) / (1000 * 60 * 60))
    : 0;
  const postedLabelFromHours = Number.isFinite(hoursInactive) && hoursInactive >= 0
    ? getRelativeTimeFromHours(hoursInactive + elapsedSinceSyncHours)
    : '';
  const dateBasedPostedLabel = getRelativeTime(job.publishedDateTime);
  const postedLabel =
    (
      (postedLabelFromHours === 'Just now' || postedLabelFromHours === 'Less than 1 hour ago') &&
      dateBasedPostedLabel &&
      dateBasedPostedLabel !== 'Just now'
    )
      ? dateBasedPostedLabel
      : (postedLabelFromHours || dateBasedPostedLabel);

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-2xl max-w-6xl w-full max-h-[95vh] overflow-hidden relative flex flex-col">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 z-10 flex-shrink-0">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <h2 className="text-lg font-semibold text-gray-900 truncate" title={job.title}>
                {job.title || 'Job Details'}
              </h2>
              <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                {postedLabel && (
                  <span>Posted {postedLabel}</span>
                )}
                {postedLabel && (
                  <span>·</span>
                )}
                <span className="inline-flex items-center gap-1">
                  <Globe className="w-3 h-3" />
                  {job.contractorSelection?.location?.countries?.length > 0
                    ? job.contractorSelection.location.countries.join(', ')
                    : 'Worldwide'}
                </span>
              </div>
            </div>
            
            {/* Navigation Controls */}
            {onNavigate && totalCount > 1 && (
              <ModalNavigation
                currentIndex={currentIndex}
                totalCount={totalCount}
                onPrevious={() => onNavigate(-1)}
                onNext={() => onNavigate(1)}
                itemName="Job"
              />
            )}
            
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors rounded-full p-1 hover:bg-gray-100 flex-shrink-0"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Two-Column Layout */}
        <div className="flex flex-1 overflow-hidden">
          {/* Main Content - Left Column */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {/* Needs to hire X Freelancers */}
            {job.freelancersToHire && job.freelancersToHire > 1 && (
              <p className="text-sm font-medium text-gray-900">
                Needs to hire {job.freelancersToHire} Freelancers
              </p>
            )}

            {/* Job Description */}
            <div className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
              {job.description}
            </div>

            {/* Bottom Details Section - Upwork format with icons */}
            <div className="border-t border-gray-200 pt-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {/* Hours per week */}
                {weeklyHours && (
                  <div className="flex items-start gap-3">
                    <Clock className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">{weeklyHours}</p>
                      <p className="text-xs text-gray-500">{contractType || 'Hourly'}</p>
                    </div>
                  </div>
                )}

                {/* Duration */}
                {duration && (
                  <div className="flex items-start gap-3">
                    <Calendar className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">{typeof duration === 'string' ? duration : duration.displayValue}</p>
                      <p className="text-xs text-gray-500">Duration</p>
                    </div>
                  </div>
                )}

                {/* Experience Level */}
                {experienceLevel && (
                  <div className="flex items-start gap-3">
                    <Award className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">{experienceLevel}</p>
                      <p className="text-xs text-gray-500">I am looking for a mix of experience and value</p>
                    </div>
                  </div>
                )}

                {/* Hourly Rate */}
                {contractType === 'Hourly' && budget && (
                  <div className="flex items-start gap-3">
                    <DollarSign className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">{budget}</p>
                      <p className="text-xs text-gray-500">Hourly</p>
                    </div>
                  </div>
                )}

                {/* Fixed Price Budget */}
                {contractType === 'Fixed-Price' && budget && (
                  <div className="flex items-start gap-3">
                    <DollarSign className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">{budget}</p>
                      <p className="text-xs text-gray-500">Fixed-price</p>
                    </div>
                  </div>
                )}

                {/* Project Type */}
                {projectType && (
                  <div className="flex items-start gap-3">
                    <Briefcase className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">{projectType}</p>
                      <p className="text-xs text-gray-500">Project Type</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Skills and Expertise - Upwork format */}
            {Array.isArray(job?.skills) && job.skills.length > 0 && (
              <div className="border-b border-gray-200 pb-4">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">Skills and Expertise</h3>
                <p className="text-xs text-gray-500 mb-2">Mandatory skills</p>
                <div className="flex flex-wrap gap-2">
                  {job.skills.map((skill, index) => (
                    <span
                      key={`${getSkillDisplay(skill)}-${index}`}
                      className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                    >
                      {getSkillDisplay(skill)}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Screening Questions - Upwork format */}
            {screeningQuestions.length > 0 && (
              <div className="border-b border-gray-200 pb-4">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">
                  Questions from the client
                </h3>
                <div className="space-y-4">
                  {screeningQuestions.map((q, index) => (
                    <div key={q.sequenceNumber || index}>
                      <p className="text-sm text-gray-900">{q.question}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Activity on this job - Upwork format */}
            <div className="border-b border-gray-200 pb-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Activity on this job</h3>
              <div className="space-y-2 text-sm">
                <p className="text-gray-600">
                  Proposals: <span className="text-gray-900 font-medium">{proposalCount || 'N/A'}</span>
                </p>
                {lastClientActivity && (
                  <p className="text-gray-600">
                    Last viewed by client: <span className="text-gray-900 font-medium">{lastClientActivity}</span>
                  </p>
                )}
                {job.activityStats?.invitesSent !== undefined && (
                  <p className="text-gray-600">
                    Invites sent: <span className="text-gray-900 font-medium">{job.activityStats.invitesSent}</span>
                  </p>
                )}
                {job.activityStats?.totalInvitedToInterview !== undefined && (
                  <p className="text-gray-600">
                    Interviewing: <span className="text-gray-900 font-medium">{job.activityStats.totalInvitedToInterview}</span>
                  </p>
                )}
                {job.activityStats?.totalUnansweredInvites !== undefined && (
                  <p className="text-gray-600">
                    Unanswered invites: <span className="text-gray-900 font-medium">{job.activityStats.totalUnansweredInvites}</span>
                  </p>
                )}
                {job.activityStats?.totalHired !== undefined && (
                  <p className="text-gray-600">
                    Hires: <span className="text-gray-900 font-medium">{job.activityStats.totalHired}</span>
                  </p>
                )}
              </div>
            </div>

            {/* Preferred Qualifications */}
            {(job.contractorSelection?.location?.countries?.length > 0 ||
              job.contractorSelection?.qualification?.englishProficiency ||
              job.contractorSelection?.qualification?.jobSuccessScore ||
              job.contractorSelection?.qualification?.hoursWorked) && (
              <div className="border-b border-gray-200 pb-4">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">Preferred qualifications</h3>
                <div className="space-y-2 text-sm">
                  {job.contractorSelection?.location?.countries?.length > 0 && (
                    <p className="text-gray-600">
                      Location: <span className="text-gray-900 font-medium">{job.contractorSelection.location.countries.join(', ')}</span>
                      {job.preferredFreelancerLocationMandatory && (
                        <span className="text-xs text-gray-500 ml-1">(required)</span>
                      )}
                    </p>
                  )}
                  {job.contractorSelection?.qualification?.englishProficiency && (
                    <p className="text-gray-600">
                      English level: <span className="text-gray-900 font-medium">{job.contractorSelection.qualification.englishProficiency}</span>
                    </p>
                  )}
                  {job.contractorSelection?.qualification?.jobSuccessScore > 0 && (
                    <p className="text-gray-600">
                      Job success score: <span className="text-gray-900 font-medium">{job.contractorSelection.qualification.jobSuccessScore}%+</span>
                    </p>
                  )}
                  {job.contractorSelection?.qualification?.hoursWorked > 0 && (
                    <p className="text-gray-600">
                      Hours billed: <span className="text-gray-900 font-medium">{job.contractorSelection.qualification.hoursWorked}+</span>
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Error Handling */}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 p-4 rounded-lg">
                <p className="flex items-center text-sm">
                  <AlertCircle className="mr-2 h-4 w-4" />
                  {error}
                </p>
              </div>
            )}

            {/* Generated Cover Letter */}
            {coverLetter && (
              <div
                ref={coverLetterRef}
                className={`relative bg-gray-50 border border-gray-200 rounded-lg p-5 transition-all duration-200 ${isEditing ? 'border-primary-300 bg-primary-50/30' : 'hover:border-gray-300'
                  }`}
                onDoubleClick={handleDoubleClick}
              >
                <h3 className="text-base font-semibold text-gray-900 mb-3 flex items-center">
                  <FileText className="mr-2 h-5 w-5 text-gray-500" />
                  Generated Cover Letter
                  {!isEditing && (
                    <Edit3 className="ml-2 h-4 w-4 text-gray-400" />
                  )}
                </h3>

                {isEditing ? (
                  <div className="relative">
                    <textarea
                      ref={textareaRef}
                      value={editedCoverLetter}
                      onChange={(e) => setEditedCoverLetter(e.target.value)}
                      onKeyDown={handleKeyDown}
                      className="w-full min-h-[200px] p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none text-sm"
                      placeholder="Edit your cover letter..."
                      autoFocus
                    />
                    <div className="mt-3 flex justify-end gap-2">
                      <button
                        onClick={handleCancel}
                        className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSave}
                        disabled={isSaving}
                        className="inline-flex items-center px-3 py-1.5 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors disabled:opacity-50"
                      >
                        <Save className="h-4 w-4 mr-1" />
                        {isSaving ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="text-sm text-gray-700 leading-relaxed">
                    {(coverLetter || '').split('\n').map((paragraph, index) => (
                      <p key={index} className="mb-3">{paragraph}</p>
                    ))}
                    <p className="text-xs text-gray-400 mt-4 italic">
                      Double-click to edit • Ctrl+Enter to save • Escape to cancel
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right Sidebar */}
          <div className="w-80 border-l border-gray-200 bg-gray-50 overflow-y-auto flex-shrink-0 p-5 space-y-5">
            {/* Action Buttons */}
            <div className="space-y-2">
              {!proposalStatus ? (
                <button
                  onClick={handleCreateProposal}
                  className="w-full inline-flex items-center justify-center px-4 py-2.5 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition-colors"
                >
                  Apply Now
                </button>
              ) : (
                <button
                  onClick={() => window.open(`/proposals/${proposalStatus._id}`, '_blank')}
                  className="w-full inline-flex items-center justify-center px-4 py-2.5 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition-colors"
                >
                  View Proposal
                </button>
              )}
              <button
                onClick={handleSaveToggle}
                disabled={savingJob}
                className={`w-full inline-flex items-center justify-center px-4 py-2.5 font-medium rounded-lg border transition-colors ${
                  isSaved
                    ? 'border-red-200 text-red-600 hover:bg-red-50'
                    : 'border-primary-600 text-primary-600 hover:bg-primary-50'
                }`}
              >
                <Heart className={`w-4 h-4 mr-2 ${isSaved ? 'fill-current' : ''}`} />
                {savingJob ? 'Saving...' : isSaved ? 'Saved' : 'Save Job'}
              </button>
              {getUpworkApplyUrl() && (
                <button
                  onClick={handleApplyOnUpwork}
                  className="w-full inline-flex items-center justify-center px-4 py-2.5 font-medium rounded-lg border border-primary-600 text-primary-600 hover:bg-primary-50 transition-colors"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Apply on Upwork
                </button>
              )}
            </div>

            {/* Cover Letter Actions */}
            <div className="border-t border-gray-200 pt-4">
              {!coverLetterPresent ? (
                <button
                  onClick={generateCoverLetter}
                  disabled={isGenerating}
                  className="w-full inline-flex items-center justify-center px-4 py-2.5 text-sm border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  {isGenerating ? 'Generating...' : 'Generate Cover Letter'}
                </button>
              ) : (
                <button
                  onClick={updateCoverLetter}
                  disabled={isGenerating}
                  className="w-full inline-flex items-center justify-center px-4 py-2.5 text-sm border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  <Edit3 className="w-4 h-4 mr-2" />
                  {isGenerating ? 'Updating...' : 'Regenerate Cover Letter'}
                </button>
              )}
            </div>

            {/* About the Client - Upwork format */}
            <div className="border-t border-gray-200 pt-4">
              <h4 className="text-lg font-semibold text-gray-900 mb-4">About the client</h4>
              <div className="space-y-4 text-sm">
                {/* Company Name */}
                {job.client?.companyName && (
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-900 font-medium">{job.client.companyName}</span>
                  </div>
                )}

                {/* Payment Verified */}
                {paymentVerified && (
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-primary-600" />
                    <span className="text-gray-700">Payment method verified</span>
                  </div>
                )}

                {/* Rating Section */}
                {clientRating !== null && clientRating !== undefined && (
                  <div>
                    <div className="flex items-center gap-2">
                      {renderStars(clientRating)}
                      <span className="text-gray-900 font-medium">
                        {typeof clientRating === 'number' ? clientRating.toFixed(1) : clientRating}
                      </span>
                    </div>
                    {job.client?.totalReviews !== undefined && (
                      <p className="text-gray-500 text-xs">
                        {typeof clientRating === 'number' ? clientRating.toFixed(2) : clientRating} of {job.client.totalReviews} review{job.client.totalReviews !== 1 ? 's' : ''}
                      </p>
                    )}
                  </div>
                )}

                {/* Location */}
                <div>
                  <p className="text-gray-900 font-medium">{getLocation()}</p>
                  {getCityWithTime() && (
                    <p className="text-gray-500 text-xs">{getCityWithTime()}</p>
                  )}
                </div>

                {/* Jobs Posted */}
                {job.client?.totalPostedJobs !== undefined && (
                  <div>
                    <p className="text-gray-900 font-medium">{job.client.totalPostedJobs} jobs posted</p>
                  </div>
                )}

                {/* Total Spent */}
                {clientTotalSpent && (
                  <div>
                    <p className="text-gray-900 font-medium">{clientTotalSpent} total spent</p>
                    {job.client?.totalHires !== undefined && (
                      <p className="text-gray-500 text-xs">
                        {job.client.totalHires} hire{job.client.totalHires !== 1 ? 's' : ''}
                      </p>
                    )}
                  </div>
                )}

                {/* Last Hired For - Client's recent activity */}
                {job.client?.lastContractTitle && (
                  <div>
                    <p className="text-gray-500 text-xs mb-1">Recently hired for:</p>
                    <p className="text-gray-900 text-sm">{job.client.lastContractTitle}</p>
                  </div>
                )}

                {/* Member Since */}
                {job.client?.memberSinceDateTime && (() => {
                  const date = new Date(job.client.memberSinceDateTime);
                  if (isNaN(date.getTime())) return null;
                  return (
                    <p className="text-gray-500 text-xs">
                      Member since {date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </p>
                  );
                })()}

                {/* Upwork shows but API does not provide: phone verified, open jobs,
                    active hires, avg hourly rate paid, total hours, industry, company size */}
              </div>
            </div>

            {/* Job Link */}
            <div className="border-t border-gray-200 pt-4">
              <h4 className="text-sm font-semibold text-gray-900 mb-2">Job link</h4>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  readOnly
                  value={getUpworkJobUrl() ? getUpworkJobUrl().replace('https://', '') : 'N/A'}
                  className="flex-1 text-xs bg-white border border-gray-200 rounded-md px-2 py-1.5 text-gray-600 truncate"
                />
                <button
                  onClick={copyJobLink}
                  disabled={!job.jobId}
                  className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors disabled:opacity-50"
                  title="Copy link"
                >
                  {linkCopied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
              {getUpworkJobUrl() && (
                <a
                  href={getUpworkJobUrl()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 mt-2"
                >
                  <ExternalLink className="w-3 h-3" />
                  View on Upwork
                </a>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Proposal Creation Modal */}
      {showProposalForm && (
        <ProposalForm
          job={job}
          coverLetter={coverLetter}
          coverLetterSource={coverLetterSource}
          onCoverLetterSourceChange={handleCoverLetterSourceChange}
          proposalData={proposalData}
          onProposalInputChange={handleProposalInputChange}
          onFileUpload={handleFileUpload}
          onRemoveAttachment={handleRemoveAttachment}
          portfolio={portfolio}
          loadingPortfolio={loadingPortfolio}
          onPortfolioSelection={handlePortfolioSelection}
          onSubmit={handleSubmitProposal}
          onCancel={() => { setShowProposalForm(false); setApplyOnUpworkMode(false); }}
          isCreatingProposal={isCreatingProposal}
          applyOnUpworkMode={applyOnUpworkMode}
          error={error}
        />
      )}
    </div>
  );
}
